import { createContext, useContext } from 'react';
import { RegistrationAPI } from '../hooks/useApproachDependencyGraph';
import { StableIdGenerator } from '../hooks/useApproachStableIds';

/**
 * Context value providing stable ID generation and dependency graph management.
 */
export interface ApproachRegistryContextValue {
  /** Stable ID generator for approaches */
  stableIds: StableIdGenerator;

  /** Dependency graph registration API */
  dependencyGraph: RegistrationAPI;
  setSourceOfChange: React.Dispatch<React.SetStateAction<string | null>>;
}

/**
 * Context for approach registration and dependency tracking.
 * Provides stable IDs and dependency graph management to the entire valuation tree.
 */
const ApproachRegistryContext = createContext<ApproachRegistryContextValue | undefined>(undefined);

/**
 * Hook to access the approach registry context.
 * Must be used within an ApproachRegistryProvider.
 *
 * @returns ApproachRegistryContextValue
 * @throws Error if used outside of provider
 *
 * @example
 * const { stableIds, dependencyGraph } = useApproachRegistry();
 * const myStableId = stableIds.getOrCreateId(approach.id || approach.panelId);
 */
export const useApproachRegistry = (): ApproachRegistryContextValue => {
  const context = useContext(ApproachRegistryContext);

  if (!context) {
    throw new Error('useApproachRegistry must be used within an ApproachRegistryProvider');
  }

  return context;
};

export default ApproachRegistryContext;
